import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteOutline, Edit, Search, ShoppingCart } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ProductModal from "../../components/ProductModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  hiddenInput: { display: "none" },
  headerControls: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap"
  }
}));

const Products = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey(value => value + 1), []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedProduct(null);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", {
          params: {
            searchParam,
            status,
            pageNumber: page + 1,
            limit: rowsPerPage
          }
        });
        setProducts(data.products);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchParam, status, page, rowsPerPage, reloadKey]);

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${selectedProduct.id}`);
      toast.success(i18n.t("products.toasts.deleted"));
      setSelectedProduct(null);
      reload();
    } catch (err) {
      toastError(err);
    }
  };

  const handleImport = async event => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error(i18n.t("products.toasts.invalidCsv"));
      return;
    }
    const form = new FormData();
    form.append("companyId", String(user.companyId));
    form.append("file", file);
    setImporting(true);
    try {
      await api.post("/products/import", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(i18n.t("products.toasts.imported"));
      setPage(0);
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setImporting(false);
    }
  };

  const formatPrice = value =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
      Number(value) / 100
    );

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("products.confirmationModal.deleteTitle")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        {i18n.t("products.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ProductModal
        open={modalOpen}
        onClose={closeModal}
        productId={selectedProduct?.id}
        onSaved={reload}
      />
      <MainHeader>
        <Title>
          <ShoppingCart /> {i18n.t("products.title")} ({count})
        </Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.headerControls}>
            <TextField
              select
              variant="outlined"
              size="small"
              label={i18n.t("products.filters.status")}
              value={status}
              onChange={event => {
                setStatus(event.target.value);
                setPage(0);
              }}
              style={{ minWidth: 140 }}
            >
              <MenuItem value="">{i18n.t("products.status.all")}</MenuItem>
              <MenuItem value="active">{i18n.t("products.status.active")}</MenuItem>
              <MenuItem value="disabled">{i18n.t("products.status.disabled")}</MenuItem>
            </TextField>
            <TextField
              placeholder={i18n.t("products.searchPlaceholder")}
              value={searchParam}
              onChange={event => {
                setSearchParam(event.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
            {user.profile === "admin" && (
              <>
                <input
                  ref={fileInputRef}
                  className={classes.hiddenInput}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleImport}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={importing}
                  onClick={() => fileInputRef.current.click()}
                >
                  {importing
                    ? i18n.t("products.buttons.importing")
                    : i18n.t("products.buttons.importCsv")}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModalOpen(true)}
            >
              {i18n.t("products.buttons.add")}
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("products.table.sku")}</TableCell>
              <TableCell align="center">{i18n.t("products.table.quantity")}</TableCell>
              <TableCell align="right">{i18n.t("products.table.price")}</TableCell>
              <TableCell align="center">{i18n.t("products.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("products.table.createdAt")}</TableCell>
              <TableCell align="center">{i18n.t("products.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map(product => (
              <TableRow key={product.id}>
                <TableCell>{product.sku}</TableCell>
                <TableCell align="center">{product.quantity}</TableCell>
                <TableCell align="right">{formatPrice(product.price)}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={product.status === "active" ? "primary" : "default"}
                    label={i18n.t(`products.status.${product.status}`)}
                  />
                </TableCell>
                <TableCell align="center">
                  {new Date(product.createdAt).toLocaleString("pt-BR")}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedProduct(product);
                      setModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedProduct(product);
                      setConfirmOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRowSkeleton key={index} columns={6} />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={(_event, newPage) => setPage(newPage)}
          onChangeRowsPerPage={event => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Paper>
    </MainContainer>
  );
};

export default Products;
